'use client';

import { useEffect, useRef, useState } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AccessiblePabxClient } from '@/hooks/useAccessiblePabxClients';

export interface TotalConnectClientSelectorProps {
  clients: AccessiblePabxClient[];
  selectedClientId: number | null;
  onSelect: (clientId: number) => void;
  isDark: boolean;
  primaryColor: string;
  /** When true, include an "All clients" pseudo-option (for Recents history scope). */
  allowAllOption?: boolean;
  /** Whether "All clients" is currently selected (only meaningful with allowAllOption). */
  allSelected?: boolean;
  onSelectAll?: () => void;
  disabled?: boolean;
}

/**
 * Compact client selector dropdown for the TotalConnectRTC toolbar.
 *
 * Renders as a single-line pill showing the current client name (or "All clients").
 * Only shown when the user has PABX/Connect access to more than one client.
 *
 * Used in two modes:
 * 1. **Calling context** (allowAllOption=false): selects which client to place
 *    outbound calls from. Drives SIP extensions, parking slots, screen-pop, etc.
 * 2. **Recents scope** (allowAllOption=true): filters call history by client or
 *    shows merged history across all accessible clients.
 */
export function TotalConnectClientSelector({
  clients,
  selectedClientId,
  onSelect,
  isDark,
  primaryColor,
  allowAllOption = false,
  allSelected = false,
  onSelectAll,
  disabled = false,
}: TotalConnectClientSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (clients.length <= 1) return null;

  const selectedClient = clients.find((c) => c.client_id === selectedClientId);
  const label = allSelected
    ? 'All clients'
    : selectedClient?.client_name ?? 'Select client';

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex max-w-[12rem] items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-medium sm:text-sm',
          'transition-colors',
          disabled && 'cursor-not-allowed opacity-50',
          isDark
            ? 'border-gray-600 bg-gray-700 text-gray-100 hover:bg-gray-600'
            : 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50',
        )}
        title={allSelected ? 'Showing calls from all clients' : `Calling from: ${label}`}
      >
        <Building2
          className="h-4 w-4 flex-shrink-0"
          style={{ color: primaryColor }}
        />
        <span className="min-w-0 flex-1 truncate text-left">{label}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 top-full z-[200] mt-1 max-h-[min(70vh,20rem)] w-56 overflow-y-auto rounded-lg border shadow-xl',
            isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white',
          )}
        >
          {allowAllOption && (
            <button
              type="button"
              onClick={() => {
                onSelectAll?.();
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors',
                allSelected
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                  : isDark
                    ? 'text-gray-200 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100',
              )}
            >
              <span className="flex items-center gap-2">
                <Building2 className="h-4 w-4 flex-shrink-0 text-blue-500" />
                All clients
              </span>
              {allSelected && <Check className="h-4 w-4 flex-shrink-0" />}
            </button>
          )}

          {allowAllOption && (
            <div
              className={cn(
                'border-b',
                isDark ? 'border-gray-700' : 'border-gray-100',
              )}
            />
          )}

          {clients.map((c) => {
            const isSelected = !allSelected && c.client_id === selectedClientId;
            return (
              <button
                key={c.client_id}
                type="button"
                onClick={() => {
                  onSelect(c.client_id);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors',
                  isSelected
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    : isDark
                      ? 'text-gray-200 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{c.client_name}</span>
                  {c.sip_extension && (
                    <span
                      className={cn(
                        'block truncate text-[10px]',
                        isDark ? 'text-gray-400' : 'text-gray-500',
                      )}
                    >
                      Ext. {c.sip_extension.extension_number}
                    </span>
                  )}
                </span>
                <span className="flex flex-shrink-0 items-center gap-1">
                  {c.is_active_client && (
                    <span
                      className={cn(
                        'rounded px-1 py-0.5 text-[9px] font-medium',
                        isDark
                          ? 'bg-gray-700 text-gray-400'
                          : 'bg-gray-100 text-gray-500',
                      )}
                    >
                      Active
                    </span>
                  )}
                  {isSelected && <Check className="h-4 w-4 flex-shrink-0" />}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
